import React from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../styles/GlobalStyles';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const HeroSection = styled.section`
  position: relative;
  width: 100vw;
  min-height: clamp(60vh, 70vh, 80vh);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${theme.colors.accentSoft} 0%, ${theme.colors.accent} 60%, ${theme.colors.secondary} 100%);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: #2C2C2C;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1.5rem;
  animation: ${fadeInUp} 1s ease-out;
`;

const LogoContainer = styled.div`
  margin-bottom: 2rem;
  animation: ${float} 3s ease-in-out infinite;
`;

const Logo = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 900;
  color: #2C2C2C;
  text-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LogoTagline = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  color: #555;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.8rem, 6vw, 4.5rem);
  font-weight: 900;
  color: #2C2C2C;
  margin-bottom: 1rem;
  letter-spacing: 2px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  background: linear-gradient(45deg, #2C2C2C, #444, #2C2C2C);
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 3s ease-in-out infinite;
`;

const HeroSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.4rem;
  font-weight: 400;
  color: #444;
  margin-bottom: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  background: ${theme.colors.sectionAlt};
  width: 100%;
  padding: clamp(5rem, 8vw, 7rem) 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${theme.colors.accent}, transparent);
  }
`;

const ContentContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 clamp(1.5rem, 4vw, 2rem);
`;

const IntroCard = styled.div`
  background: linear-gradient(135deg, #fff 0%, #fefefe 100%);
  border-radius: 24px;
  padding: 3.5rem;
  margin-bottom: 4rem;
  box-shadow: 0 12px 40px rgba(34,49,58,0.1);
  border: 2px solid ${theme.colors.accent}33;
  text-align: center;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${theme.colors.accentSoft}, ${theme.colors.accent}, ${theme.colors.accentSoft});
  }
  
  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
  }
`;

const IntroText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  color: #333;
  line-height: 1.8;
  margin-bottom: 0;
  font-weight: 500;
`;

const PrinciplesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  margin-bottom: 4rem;
`;

const PrincipleCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 8px 32px rgba(34,49,58,0.08);
  border-left: 5px solid ${theme.colors.accent};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out both;
  
  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
  &:nth-child(5) { animation-delay: 0.5s; }
  &:nth-child(6) { animation-delay: 0.6s; }
  &:nth-child(7) { animation-delay: 0.7s; }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, ${theme.colors.accent}1a 0%, transparent 70%);
    border-radius: 50%;
    transform: translate(30px, -30px);
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 48px rgba(34,49,58,0.15);
    border-left-color: ${theme.colors.accentSoft};
  }
  
  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
  }
`;

const PrincipleIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, ${theme.colors.accentSoft} 0%, ${theme.colors.accent} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  color: #2C2C2C;
  font-weight: 900;
  box-shadow: 0 4px 16px rgba(255, 215, 0, 0.3);
`;

const PrincipleTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.colors.accent};
  margin-bottom: 1.2rem;
  position: relative;
`;

const PrincipleText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.15rem;
  color: #444;
  line-height: 1.8;
  margin-bottom: 0;
`;

const CoreValuesSection = styled.div`
  background: linear-gradient(135deg, #2C2C2C 0%, #1a1a1a 100%);
  border-radius: 24px;
  padding: 4rem 3rem;
  margin: 4rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out 0.8s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 40%, rgba(255,215,0,0.1) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(255,165,0,0.08) 0%, transparent 50%);
  }
  
  @media (max-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const CoreValuesTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 2.2rem;
  font-weight: 700;
  color: ${theme.colors.accent};
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
`;

const CoreValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 2rem;
  position: relative;
  z-index: 1;
`;

const CoreValue = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,215,0,0.2);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255,215,0,0.1);
    transform: translateY(-3px);
  }
`;

const CoreValueTitle = styled.h4`
  font-family: 'Playfair Display', serif;
  font-size: 1.3rem;
  color: ${theme.colors.accent};
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

const CoreValueText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: #ccc;
  margin: 0;
  line-height: 1.6;
`;

const ClosingSection = styled.div`
  text-align: center;
  padding: 3rem;
  background: linear-gradient(135deg, rgba(255,215,0,0.05) 0%, rgba(255,165,0,0.02) 100%);
  border-radius: 20px;
  border: 1px solid rgba(255,215,0,0.1);
  animation: ${fadeInUp} 0.8s ease-out 0.9s both;
`;

const ClosingText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.2rem;
  color: #333;
  line-height: 1.8;
  margin-bottom: 2rem;
  font-style: italic;
  font-weight: 500;
`;

const ContactButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, ${theme.colors.accentSoft} 0%, ${theme.colors.accent} 100%);
  color: #2C2C2C;
  padding: 1rem 2.5rem;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(255, 215, 0, 0.3);
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 215, 0, 0.4);
    background: linear-gradient(135deg, ${theme.colors.accent} 0%, ${theme.colors.accentSoft} 100%);
  }
`;

const ContentCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 2.5rem;
  box-shadow: 0 8px 32px rgba(34,49,58,0.08);
  border: 1px solid rgba(255, 215, 0, 0.1);
  animation: ${fadeInUp} 0.8s ease-out both;
  
  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
  }
`;

const SubsectionTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.colors.accent};
  margin-bottom: 1.5rem;
  border-bottom: 2px solid ${theme.colors.accent};
  padding-bottom: 0.5rem;
`;

const ContentText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.15rem;
  color: #444;
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const CodeOfConductPage: React.FC = () => {
  const principles = [
    {
      icon: "❤️",
      title: "We Put People First",
      text: "Our clients and colleagues are at the heart of everything we do. We listen carefully, respond thoughtfully, and work collaboratively to deliver meaningful solutions. We value each relationship and treat everyone with dignity, empathy, and respect."
    },
    {
      icon: "⚡",
      title: "We Act with Integrity", 
      text: "Honesty and transparency are non-negotiable. We make ethical choices, even when it's difficult or inconvenient. We protect confidential information, avoid conflicts of interest, and always strive to do the right thing."
    },
    {
      icon: "🌍",
      title: "We Embrace Diversity and Inclusion",
      text: "We believe that different perspectives drive better outcomes. We welcome diverse backgrounds, ideas, and experiences, creating an environment where everyone feels safe to contribute and grow."
    },
    {
      icon: "🏆",
      title: "We Deliver Excellence",
      text: "Our clients rely on us for quality and reliability. We take pride in our work, hold ourselves accountable for results, and never stop looking for ways to improve. We honor our commitments and approach every challenge with a solution-focused mindset."
    },
    {
      icon: "🛡️",
      title: "We Foster a Safe and Positive Workplace",
      text: "Harassment, discrimination, and bias have no place at DeArc Global. We promote psychological safety, encourage open communication, and support each other's well-being."
    },
    {
      icon: "🌱",
      title: "We Are Responsible Citizens",
      text: "We recognize our role in the broader community. We comply with all laws and regulations, act responsibly with resources, and seek opportunities to make a positive impact beyond our business."
    }
  ];

  const coreValues = [
    { title: "Integrity", text: "Honest & Transparent" },
    { title: "Innovation", text: "Creative Solutions" },
    { title: "Passion", text: "Dedicated Excellence" },
    { title: "Accountability", text: "Responsible Actions" },
    { title: "Growth", text: "Continuous Improvement" }
  ];

  return (
    <>
      <HeroSection>
        <HeroContent>
          <LogoContainer>
            <Logo>DeArc Global</Logo>
            <LogoTagline>Excellence in Talent Solutions</LogoTagline>
          </LogoContainer>
          <HeroTitle>Code of Conduct</HeroTitle>
          <HeroSubtitle>
            #ValuesInPractice
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <ContentContainer>
          <IntroCard>
            <IntroText>
              At DeArc Global, our reputation is built on trust, innovation, and a commitment to doing what's right. This Code of Conduct is more than a set of rules—it's a reflection of our shared values and the culture we strive to create every day. It guides our decisions, shapes our relationships, and defines how we do business.
            </IntroText>
          </IntroCard>

          <ContentCard>
            <SubsectionTitle>Who We Are</SubsectionTitle>
            <ContentText>
              At DeArc Global, our reputation is built on trust, innovation, and a commitment to doing what's right. This Code of Conduct is more than a set of rules—it's a reflection of our shared values and the culture we strive to create every day. It guides our decisions, shapes our relationships, and defines how we do business.
            </ContentText>
          </ContentCard>

          <ContentCard>
            <SubsectionTitle>Our Commitment</SubsectionTitle>
            <ContentText>
              We hold ourselves to the highest standards of integrity and professionalism. Every interaction—with clients, colleagues, partners, and the wider community—should reflect our dedication to fairness, respect, and ethical conduct.
            </ContentText>
          </ContentCard>

          <PrinciplesGrid>
            {principles.map((principle, index) => (
              <PrincipleCard key={index}>
                <PrincipleIcon>{principle.icon}</PrincipleIcon>
                <PrincipleTitle>{principle.title}</PrincipleTitle>
                <PrincipleText>{principle.text}</PrincipleText>
              </PrincipleCard>
            ))}
          </PrinciplesGrid>

          <ContentCard>
            <SubsectionTitle>Living Our Values</SubsectionTitle>
            <ContentText>
              Every member of DeArc Global is a steward of our culture. If you see something that doesn't align with our Code, speak up—your voice matters. We provide confidential channels for raising concerns and protect those who do so in good faith.
            </ContentText>
          </ContentCard>

          <CoreValuesSection>
            <CoreValuesTitle>Our Promise</CoreValuesTitle>
            <ContentText style={{ color: '#ccc', marginBottom: '2rem', position: 'relative', zIndex: 1 }}>
              We are united by our core values:
            </ContentText>
            <CoreValuesGrid>
              {coreValues.map((value, index) => (
                <CoreValue key={index}>
                  <CoreValueTitle>{value.title}</CoreValueTitle>
                  <CoreValueText>{value.text}</CoreValueText>
                </CoreValue>
              ))}
            </CoreValuesGrid>
            <ContentText style={{ color: '#ccc', marginTop: '2rem', position: 'relative', zIndex: 1, fontStyle: 'italic' }}>
              By living these values every day, we earn the trust of our clients, partners, and each other. Together, we build a workplace—and a world—where we can all thrive.
            </ContentText>
          </CoreValuesSection>

          <ClosingSection>
            <ClosingText>
              For questions or to report concerns, please contact us at:
            </ClosingText>
            <ContactButton href="mailto:info@dearcglobal.com">
              info@dearcglobal.com
            </ContactButton>
          </ClosingSection>
        </ContentContainer>
      </ContentSection>
    </>
  );
};

export default CodeOfConductPage; 